import React, { useEffect, useState } from 'react';
import { Breadcrumb, Row, Col, Form } from 'react-bootstrap';
import { Link, useParams, useHistory } from 'react-router-dom';
import BreadcrumbsPortal from '@rd-web-markets/shared/dist/util/BreadcrumbsPortal';
import { Loading } from '@rd-web-markets/shared/dist/util';
import Modal from '@rd-web-markets/shared/dist/util/Modal';
import { SaveButton } from '@rd-web-markets/shared/dist/util/buttons';
import { useTranslation } from 'react-i18next';
import reportIssuesService from '@rd-web-markets/shared/dist/services/report_issues.service';
import { UserService } from '@rd-web-markets/shared/dist/services/user.service';
import BackButton from '@rd-web-markets/shared/dist/util/buttons/BackButton';

const REPORT_TYPES = ['bug', 'improvement'];
const STATUSES = ['open', 'in_progress', 'resolved'];

export default function ReportIssueDetailPage({ accountType }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const [report, setReport] = reportIssuesService.useFetchOne(id, setLoading);
  const updateReport = reportIssuesService.useUpdate(setLoading, setReport);
  const deleteImage = reportIssuesService.useDeleteImage(setLoading);

  useEffect(() => {
    UserService.getCurrent().then((res) => setCurrentUser(res?.user));
  }, []);

  useEffect(() => {
    if (report && !formData) {
      setFormData({
        title: report.title,
        description: report.description,
        report_type: report.report_type,
        status: report.status,
        assignee_user_id: report.assignee_user_id || ''
      });
    }
  }, [report, formData]);

  const canEdit = currentUser && report && (
    currentUser.account_type === 'admin' || currentUser.id === report.reporter_user_id
  );

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleRemoveImage = async (imageId) => {
    if (!window.confirm(t('confirm_remove_image'))) return;
    const updated = await deleteImage({ reportId: id, imageId });
    setReport(updated);
  };

  const handleSubmit = async () => {
    const payload = new FormData();
    payload.append('report[title]', formData.title);
    payload.append('report[description]', formData.description);
    payload.append('report[report_type]', formData.report_type);
    payload.append('report[status]', formData.status);
    if (formData.assignee_user_id) payload.append('report[assignee_user_id]', formData.assignee_user_id);
    selectedFiles.forEach(file => payload.append('report[images][]', file));

    await updateReport({ id, formData: payload });
    setSelectedFiles([]);
  };

  const handleBack = () => {
    history.push(`/${accountType}/report_issues`);
  };

  if (loading || !report || !formData) return <Loading />;

  return (
    <>
      <BreadcrumbsPortal>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin' }}>Home</Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/admin/report_issues' }}>{t('reports')}</Breadcrumb.Item>
          <Breadcrumb.Item active>{report.title}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbsPortal>

      <Row className="justify-content-center mx-0 mt-4">
        <Col md={10}>
          <h4 className="mb-3">{canEdit ? t('edit_report') : t('view_report')}</h4>

          <Form>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('title')}</Form.Label>
                  <Form.Control data-testid="detail_title_input" disabled={!canEdit} value={formData.title} onChange={(e) => handleChange('title', e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('report_type')}</Form.Label>
                  <Form.Select data-testid="detail_report_type_select" disabled={!canEdit} value={formData.report_type} onChange={(e) => handleChange('report_type', e.target.value)}>
                    {REPORT_TYPES.map(type => <option key={type} value={type}>{t(type)}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('status')}</Form.Label>
                  <Form.Select data-testid="detail_status_select" disabled={!canEdit} value={formData.status} onChange={(e) => handleChange('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{t(s)}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('description')}</Form.Label>
                  <Form.Control
                    data-testid="detail_description_input"
                    as="textarea"
                    rows={6}
                    disabled={!canEdit}
                    style={{ height: 'auto' }}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-4">{t('screenshots')}</h6>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {report.images?.map(img => (
                <div key={img.id} style={{ position: 'relative' }}>
                  <img
                    data-testid={`image_thumb_${img.id}`}
                    src={img.url}
                    alt=""
                    style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer', borderRadius: 4, border: '1px solid #dee2e6' }}
                    onClick={() => setActiveImage(img)}
                  />
                  {canEdit && (
                    <button
                      data-testid={`image_remove_${img.id}`}
                      type="button"
                      className="btn-close"
                      style={{ position: 'absolute', top: 4, right: 4, background: 'white', borderRadius: '50%', padding: 4 }}
                      onClick={() => handleRemoveImage(img.id)}
                    />
                  )}
                </div>
              ))}
            </div>

            {canEdit && (
              <Form.Group className="mb-3">
                <Form.Label>{t('add_screenshots')}</Form.Label>
                <Form.Control
                  data-testid="detail_screenshot_input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedFiles(e.target.files[0] ? [e.target.files[0]] : [])}
                />
                {selectedFiles.length > 0 && (
                  <Form.Text className="text-muted">{selectedFiles.map(f => f.name).join(', ')}</Form.Text>
                )}
              </Form.Group>
            )}

            <div className="d-flex justify-content-end gap-2">
              <BackButton data-testid="back_button" onClick={handleBack} />
              {canEdit && <SaveButton data-testid="detail_save_button" onClick={handleSubmit} text={t('save_changes')} />}
            </div>
          </Form>
        </Col>
      </Row>

      {activeImage && (
        <Modal
          size="lg"
          title={null}
          show={!!activeImage}
          onHide={() => setActiveImage(null)}
          closeText={t('close')}
          renderBody={() => (
            <div>
              <img
                data-testid="modal_image"
                src={activeImage.url}
                alt=""
                style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
              />
              <div className="d-flex justify-content-center mt-3">
                <a
                  data-testid="download_link"
                  href={activeImage.url}
                  download={activeImage.filename}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  {t('download')}
                </a>
              </div>
            </div>
          )}
        />
      )}
    </>
  );
}
